import { useAppDispatch, useAppSelector } from "../../../../../../../app/hooks";
import { useContext, useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { NetworkErrorAlertContext } from "../../../../../../../context/NetworkErrorAlert";
import { AttachmentFileType, CNY_PRODUCT_CATEGORY } from "../../../../../const";
import { merchantBusinessKeys } from "../../../../../../Settings/settingsSlice";
import dayjs from "dayjs";
import { savedCNYData } from "../../../../../redux/formDataSlice";
import { ReactComponent as InfoIcon } from "../../../../../../../assets/icons/info.svg";

import {
  fetchCNYCardBank,
  uploadCNYMerchantFile,
} from "../../../../../redux/slice";
import {
  ButtonsWrapper,
  ContentWrapper,
  Heading,
  InputWrapper,
  ItemWrapper,
  Error,
  NotificationWrapper,
} from "../../styles";
import CustomSelect from "../../../../../../../components/common/CustomSelect";
import React from "react";
import { Input } from "../../../../../../../components/common/Input";
import { DocumentUpload } from "../../../../../components/DocumentUpload";
import InputAndSelect from "../../../../../../../components/common/InputAndSelect";
import CustomDatePicker from "../../../../../../../components/common/CustomDateRangePicker/CustomDatePicker";
import { Button } from "../../../../../../../components/common/Button";
import Loader from "../../../../../../../components/common/Loader";
import {
  CNYProps,
  FormInputs,
  UploadCNYMerchantFilePayload,
  UserData,
  DocumentDetails,
} from "../../../../../../../types/walletTypes";

const buildDocumentDetails = (
  form: FormInputs,
  tradeType: string,
): DocumentDetails => ({
  totalAmount: String(form.totalBeneficiaryAmount),
  attachments: [{ fileId: String(form.uploadedFileId) }],
  orderNumber: form.orderNumber!,
  orderTime: dayjs(form.orderDate).format("YYYY-MM-DD"),
  orderCurrency: "CNY",
  tradeType: tradeType,
  paymentType: "COLLECT",
  products: [
    {
      name: form.productName!,
      quantity: form.productNumber!,
      productCategory: form.productCategory,
      website: form.website,
    },
  ],
  customs: {
    customsDecNo: form.customsDeclarationNumber!,
  },
  logistics: {
    company: form.logisticsCompany,
    orderNo: form.logisticsOrderNumber,
    shipDate: dayjs(form.orderDate).format("YYYY-MM-DD"),
  },
});

const BusinessPaymentDetailsForm = ({
  handleNext,
  handlePrevious,
}: CNYProps) => {
  const getSavedData = useAppSelector((state) => state.formData.cnyData);
  const { orderDate, ...rest } = getSavedData;
  const defaultValues = { ...rest };

  const {
    handleSubmit,
    control,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FormInputs>({ defaultValues });
  const { loading } = useAppSelector((state) => state?.wallets || {});
  const { keys } = useAppSelector((state) => state.settings);
  const [userData, setUserData] = useState<UserData>(null);
  const { businessId } = useAppSelector((state) => state.users);
  const { onShowAlert: onShowErrorAlert } = useContext(
    NetworkErrorAlertContext,
  );

  const dispatch = useAppDispatch();
  const publicKey = keys?.data?.publicKey;

  useEffect(() => {
    if (businessId) {
      dispatch(merchantBusinessKeys(businessId));
    }
  }, [businessId, dispatch]);

  const BUSINESS_PRODUCT_PURPOSE = getSavedData?.purpose === "ECOMMERCE";

  const onSubmit: SubmitHandler<FormInputs> = (data) => {
    const document = buildDocumentDetails(
      data,
      BUSINESS_PRODUCT_PURPOSE ? "GOODS" : "GENERAL_GOODS",
    );

    dispatch(
      savedCNYData({
        ...data,
        document,
        orderDate: dayjs(data.orderDate).format("YYYY-MM-DD"),
      }),
    );

    handleNext?.();
  };

  const docType = watch("commercialDocument");

  const handleFileChange = async (title?: string, file?: File | null) => {
    if (!file) return;

    const action = await dispatch(
      uploadCNYMerchantFile({
        file,
        purpose: getSavedData.purpose,
        attachmentType: docType,
        xAuthToken: publicKey,
      }),
    );

    if (uploadCNYMerchantFile.rejected.match(action)) {
      onShowErrorAlert("error", action.payload as string);
    } else if (uploadCNYMerchantFile.fulfilled.match(action)) {
      const { id }: UploadCNYMerchantFilePayload = action.payload;

      setValue("uploadedFileId", id);

      dispatch(
        savedCNYData({
          commercialDocument: id,
        }),
      );
    }
  };

  const handleDataUpdate = (field: keyof UserData, value: any) => {
    setUserData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const currencyOptions = [{ label: "CNY", value: "CNY" }];

  useEffect(() => {
    if (businessId) {
      dispatch(
        fetchCNYCardBank({
          xAuthToken: keys?.data?.publicKey,
          includeType: !getSavedData.accountType,
        }),
      ).then((action) => {
        if (fetchCNYCardBank.fulfilled.match(action)) {
          const uniqueBanks = Array.from(
            new Map(
              action.payload.data.map((bank: any) => [bank.code, bank]),
            ).values(),
          );

          handleDataUpdate("banks", uniqueBanks);
        }
      });
    }
  }, [businessId]);

  const bankOptions = userData?.banks?.map((bank) => ({
    value: bank.code,
    label: bank.name,
  }));

  return (
    <ContentWrapper>
      <Loader isLoading={loading} />

      <InputWrapper>
        <Controller
          name="bankCode"
          control={control}
          rules={{ required: "Bank is required" }}
          render={({ field }) => (
            <CustomSelect
              value={field.value}
              options={bankOptions}
              onChange={(selectedValue) => {
                field.onChange(selectedValue);
                const selectedBank = userData?.banks?.find(
                  (b) => b.code === selectedValue,
                );
                setValue("bankName", selectedBank?.name || "");
              }}
              $width="100%"
              placeholder="Bank name"
              showSearch
              filterOption={(inputValue, option) =>
                option.label.toLowerCase().includes(inputValue.toLowerCase())
              }
              lightBorder
            />
          )}
        />
        <Error>{errors?.bankCode?.message}</Error>
      </InputWrapper>

      <InputWrapper>
        <Controller
          name={"bankAccountName"}
          control={control}
          rules={{ required: "Field is required" }}
          render={({ field }) => (
            <Input
              type="text"
              label="Account name"
              value={field.value}
              onChange={field.onChange}
              hasError={!!errors?.accountName?.firstName}
              labelColor="#FFFFFF"
            />
          )}
        />

        <NotificationWrapper>
          <InfoIcon />
          <p>The name must be in Chinese</p>
        </NotificationWrapper>
        <Error>{errors?.bankAccountName?.message}</Error>
      </InputWrapper>

      <InputWrapper>
        <Controller
          name="accountNumber"
          control={control}
          rules={{ required: "Field is required" }}
          render={({ field }) => (
            <Input
              type="text"
              label="Account number"
              value={field.value}
              onChange={field.onChange}
              hasError={!!errors?.accountNumber}
              labelColor="#FFFFFF"
            />
          )}
        />
        <Error>{errors?.accountNumber?.message}</Error>
      </InputWrapper>

      {!BUSINESS_PRODUCT_PURPOSE && (
        <ItemWrapper>
          <InputWrapper>
            <Controller
              name="logisticsCompany"
              control={control}
              rules={{ required: "field is required" }}
              render={({ field }) => (
                <Input
                  type="text"
                  label="Logistics company"
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />
            <Error>{errors?.logisticsOrderNumber?.message}</Error>
          </InputWrapper>

          <InputWrapper>
            <Controller
              name="logisticsOrderNumber"
              control={control}
              rules={{ required: "field is required" }}
              render={({ field }) => (
                <Input
                  type="text"
                  label="Logistics order number"
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />
            <Error>{errors?.logisticsOrderNumber?.message}</Error>
          </InputWrapper>
        </ItemWrapper>
      )}

      {!BUSINESS_PRODUCT_PURPOSE && (
        <InputWrapper>
          <Controller
            name="customsDeclarationNumber"
            control={control}
            rules={{ required: "Customs declaration number is required" }}
            render={({ field }) => (
              <Input
                label="Custom's description number"
                value={field.value}
                onChange={field.onChange}
                hasError={!!errors?.customsDeclarationNumber}
              />
            )}
          />
          <Error>{errors?.customsDeclarationNumber?.message}</Error>
        </InputWrapper>
      )}

      <InputWrapper>
        <Heading>Attachment file type</Heading>

        <Controller
          name="commercialDocument"
          control={control}
          rules={{ required: "Commercial document is required" }}
          render={({ field }) => (
            <CustomSelect
              value={field.value}
              options={
                BUSINESS_PRODUCT_PURPOSE
                  ? AttachmentFileType.filter(
                      (item) => item.value !== "CUSTOMS_DECLARATION",
                    )
                  : AttachmentFileType
              }
              onChange={field.onChange}
              $width="100%"
              placeholder="Attachment file type"
              lightBorder
            />
          )}
        />
        {<Error>{errors?.commercialDocument?.message}</Error>}
      </InputWrapper>

      <InputWrapper>
        <Controller
          name="document"
          control={control}
          rules={{
            required: "Please upload a document",
            validate: (file) => !!file || "Uploaded file must be valid",
          }}
          render={({ field }) => (
            <DocumentUpload
              showExtra
              onFileChange={(file, event) => {
                event?.stopPropagation();
                field.onChange(file);
                handleFileChange(docType, file);
              }}
            />
          )}
        />
        <Error>{errors?.document?.message}</Error>
      </InputWrapper>

      <ItemWrapper>
        <InputWrapper>
          <Controller
            name="productName"
            control={control}
            rules={{ required: "Product name is required" }}
            render={({ field }) => (
              <Input
                type="text"
                label="Product name"
                value={field.value}
                onChange={field.onChange}
                hasError={!!errors.productName}
              />
            )}
          />
          <Error>{errors?.productName?.message}</Error>
        </InputWrapper>

        <InputWrapper>
          <Controller
            name="productNumber"
            control={control}
            rules={{ required: "Product quantity is required" }}
            render={({ field }) => (
              <Input
                type="text"
                label="Product quantity"
                value={field.value}
                onChange={field.onChange}
                hasError={!!errors.productNumber}
              />
            )}
          />
          <Error>{errors?.productNumber?.message}</Error>
        </InputWrapper>
      </ItemWrapper>

      {!BUSINESS_PRODUCT_PURPOSE && (
        <InputWrapper>
          <Controller
            name="productCategory"
            control={control}
            rules={{ required: "Product category is required" }}
            render={({ field }) => (
              <CustomSelect
                value={field.value}
                options={CNY_PRODUCT_CATEGORY}
                onChange={field.onChange}
                $width="100%"
                placeholder="Product category"
                lightBorder
              />
            )}
          />
          {<Error>{errors?.productCategory?.message}</Error>}
        </InputWrapper>
      )}

      {BUSINESS_PRODUCT_PURPOSE && (
        <InputWrapper>
          <Controller
            name="website"
            control={control}
            rules={{ required: "Field is required" }}
            render={({ field }) => (
              <Input
                type="text"
                label="Website"
                value={field.value}
                onChange={field.onChange}
                hasError={!!errors?.website}
                labelColor="#FFFFFF"
              />
            )}
          />
          <Error>{errors?.website?.message}</Error>
        </InputWrapper>
      )}

      <InputWrapper>
        <Controller
          name="totalBeneficiaryAmount"
          control={control}
          rules={{ required: "Amount is required" }}
          render={({ field }) => (
            <InputAndSelect
              amountValue={field.value}
              width="100%"
              placeholder="Total Amount"
              options={currencyOptions}
              selectValue="CNY"
              onAmountChange={field.onChange}
            />
          )}
        />
        <Error>{errors?.totalBeneficiaryAmount?.message}</Error>
      </InputWrapper>

      <ItemWrapper>
        <InputWrapper>
          <Controller
            name="orderNumber"
            control={control}
            rules={{ required: "Field is required" }}
            render={({ field }) => (
              <Input
                type="text"
                label="Order number"
                value={field.value}
                onChange={field.onChange}
                hasError={!!errors?.orderNumber}
              />
            )}
          />
          <Error>{errors?.orderNumber?.message}</Error>
        </InputWrapper>

        <InputWrapper>
          <Controller
            name="orderDate"
            control={control}
            rules={{ required: "field is required" }}
            render={({ field }) => (
              <CustomDatePicker
                value={field.value}
                onChange={field.onChange}
                placeholder={`${
                  BUSINESS_PRODUCT_PURPOSE ? "Order date" : "Ship date"
                }`}
              />
            )}
          />
          <Error>{errors?.orderDate?.message}</Error>
        </InputWrapper>
      </ItemWrapper>

      <ButtonsWrapper>
        <Button onClick={handlePrevious} theme="alternate" height="48px">
          Back
        </Button>
        <Button
          onClick={handleSubmit(onSubmit)}
          label="Continue"
          height="48px"
        />
      </ButtonsWrapper>
    </ContentWrapper>
  );
};

export default BusinessPaymentDetailsForm;
